import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { type LoginInput, loginInputSchema } from 'used-vehicle-contracts';
import { isUnauthorizedError } from '../../apis/orpc-client';
import { useAuth } from '../../auth/AuthContext';
import { CHANGE_PASSWORD_PATH } from '../../auth/ProtectedRoute';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

interface LocationState {
  from?: string;
}

export const LoginPage = () => {
  const { t } = useI18nTranslation();
  const { isAuthenticated, isBootstrapping, mustChangePassword, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { email: '', password: '' },
  });

  if (isAuthenticated && !isBootstrapping) {
    return (
      <Navigate
        to={mustChangePassword ? CHANGE_PASSWORD_PATH : ((location.state as LocationState | null)?.from ?? '/dashboard')}
        replace
      />
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    try {
      const user = await login(values);

      navigate(user.mustChangePassword ? CHANGE_PASSWORD_PATH : ((location.state as LocationState | null)?.from ?? '/dashboard'), {
        replace: true,
      });
    } catch (error) {
      setSubmitError(isUnauthorizedError(error) ? t('login.invalidCredentials') : t('login.failed'));
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <p className="text-lg font-semibold tracking-tight text-foreground">{t('app.name')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('app.tagline')}</p>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-card-foreground">{t('login.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('login.subtitle')}</p>

          <form
            onSubmit={onSubmit}
            noValidate
            className="mt-6 space-y-4"
          >
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-card-foreground"
              >
                {t('login.email')}
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                dir="ltr"
                className="text-start"
                placeholder={t('login.emailPlaceholder')}
                hasError={Boolean(errors.email)}
                {...register('email')}
              />
              {errors.email?.message ? <p className="text-xs text-destructive">{errors.email.message}</p> : null}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-card-foreground"
              >
                {t('login.password')}
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                dir="ltr"
                className="text-start"
                placeholder={t('login.passwordPlaceholder')}
                hasError={Boolean(errors.password)}
                {...register('password')}
              />
              {errors.password?.message ? <p className="text-xs text-destructive">{errors.password.message}</p> : null}
            </div>

            {submitError ? (
              <p
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
              >
                {submitError}
              </p>
            ) : null}

            <Button
              type="submit"
              isLoading={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>
        </div>

        <div className="mt-4 flex justify-center">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
};
